import { useCallback, useState } from 'react';
import { PlaidLinkOnSuccessMetadata } from 'react-plaid-link';
import { useMutation, useQueryClient } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import PATHS from '@/common/paths';
import { PaymentMethodType } from '@/modules/user/user.types';

export interface PlaidPaymentMethodRequestData {
  plaidMetadata: PlaidLinkOnSuccessMetadata;
  isDefault?: boolean;
  accountId?: string;
  isTryAgainFlow?: boolean;
}

interface PlaidPaymentMethodResponse {
  payment_ids: number[];
}

const formatDataToCreate = (value: PlaidPaymentMethodRequestData, skipNotificationSending?: boolean) => {
  return {
    ...(skipNotificationSending ? { skip_notification_sending: true } : {}),
    payment_method_type: PaymentMethodType.DIRECT_DEPOSIT,
    is_default: value?.isDefault,
    plaid_metadata: value.plaidMetadata,
    ...(value?.accountId ? { account_id: value?.accountId } : {}),
    ...(value?.isTryAgainFlow ? { is_try_again_flow: true } : {}),
  };
};

const useAddPlaidPaymentMethod = (
  onSuccess?: (paymentMethodId?: string, paymentMethodIds?: string[]) => void
) => {
  const queryClient = useQueryClient();
  const [isLoading, setLoading] = useState(false);
  const { mutateAsync, isSuccess, isError } = useMutation(
    (value: PlaidPaymentMethodRequestData) =>
      api.post<PlaidPaymentMethodResponse>(PATHS.PLAID_PAYMENT_ACCOUNT, formatDataToCreate(value)),
    {
      onSuccess: (value) => {
        const paymentMethodIds = value?.data?.payment_ids
          ? value?.data?.payment_ids.map((id) => toString(id))
          : undefined;
        queryClient.invalidateQueries({
          queryKey: [PATHS.PAYMENT_METHODS, { refetchInactive: true }],
        });
        setLoading(false);

        if (onSuccess) {
          onSuccess(undefined, paymentMethodIds);
        }
      },
      onError: () => {
        setLoading(false);
      },
    }
  );
  const save = useCallback(
    async (value: PlaidPaymentMethodRequestData) => {
      setLoading(true);

      const { data } = await mutateAsync({
        ...value,
      });

      return data;
    },
    [mutateAsync]
  );

  return { isLoading, save, isSuccess, isError };
};

export default useAddPlaidPaymentMethod;
